import type { FreeformKind, InkStroke } from '../model/schema.js';
import { decodePoints, encodePoints } from './codec.js';
import type { Affine, RotatedRect } from './geometry.js';
import {
  applyAffine,
  compose,
  invert,
  pointInRotatedRect,
  rectCenter,
  rotation,
  scaling,
  translation,
} from './geometry.js';
import type { InkPoint, Pt } from './types.js';

/** A free-form element ink can attach to (stage items and the frame never capture ink, I3). */
export interface AttachElement extends RotatedRect {
  id: string;
  kind: FreeformKind;
}

/** Share of a stroke's points that must lie inside an element (decision I3). */
export const ATTACH_RATIO = 0.8;

/** Connectors have no area; zero-sized elements cannot be scaled. */
export function canAttachTo(el: Pick<AttachElement, 'kind' | 'w' | 'h'>): boolean {
  return (
    el.kind !== 'arrow' && el.kind !== 'line' && el.kind !== 'dimension' && el.w > 0 && el.h > 0
  );
}

/** Fraction of `points` inside the element's rotated bounds. */
export function insideRatio(points: readonly Pt[], el: RotatedRect): number {
  if (points.length === 0) return 0;
  return points.filter((p) => pointInRotatedRect(p, el)).length / points.length;
}

/**
 * The element a freshly drawn stroke (board coordinates) attaches to: ≥ 80 % of its points
 * inside the element's rotated bounds; `elements` are in render order, so the topmost wins.
 */
export function attachTarget(
  points: readonly Pt[],
  elements: readonly AttachElement[],
  ratio = ATTACH_RATIO,
): string | null {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i]!;
    if (canAttachTo(el) && insideRatio(points, el) >= ratio) return el.id;
  }
  return null;
}

export interface Size {
  w: number;
  h: number;
}

/**
 * Element-local → board transform. Local coordinates have their origin in the element centre and
 * are unrotated; `size` is the element size the local points were recorded at, so a resized
 * element scales the positions (decision J4).
 */
export function elementTransform(el: RotatedRect, size: Size = el): Affine {
  const c = rectCenter(el);
  return compose(
    translation(c.x, c.y),
    compose(rotation(el.rotation), scaling(el.w / size.w, el.h / size.h)),
  );
}

function mapPoints(points: readonly InkPoint[], m: Affine): InkPoint[] {
  return points.map((p) => ({ ...p, ...applyAffine(m, p) }));
}

export function localToBoard(
  points: readonly InkPoint[],
  el: RotatedRect,
  size?: Size,
): InkPoint[] {
  return mapPoints(points, elementTransform(el, size));
}

export function boardToLocal(points: readonly InkPoint[], el: RotatedRect): InkPoint[] {
  return mapPoints(points, invert(elementTransform(el)));
}

/** Elements by id (e.g. `BoardData.freeform`). */
export type ElementLookup = Readonly<Record<string, RotatedRect | undefined>>;

/**
 * Board coordinates of a stroke. Attached strokes are transformed with their element; null when
 * that element is missing (deleted remotely, filtered out) – such strokes are not drawn.
 */
export function strokeBoardPoints(
  stroke: InkStroke,
  elements: ElementLookup = {},
): InkPoint[] | null {
  const points = decodePoints(stroke.points);
  if (stroke.attachedTo === undefined) return points;
  const el = elements[stroke.attachedTo];
  return el ? localToBoard(points, el, stroke.attachedSize ?? el) : null;
}

/** Replaces a stroke's geometry by board points, keeping its attachment (converted to local). */
export function withBoardPoints(
  stroke: InkStroke,
  points: readonly InkPoint[],
  elements: ElementLookup = {},
): InkStroke {
  const el = stroke.attachedTo === undefined ? undefined : elements[stroke.attachedTo];
  if (!el) {
    const { attachedTo: _a, attachedSize: _s, ...rest } = stroke;
    return { ...rest, points: encodePoints(points) };
  }
  return {
    ...stroke,
    points: encodePoints(boardToLocal(points, el)),
    attachedSize: { w: el.w, h: el.h },
  };
}

/** Attaches a stroke (given in board coordinates or attached elsewhere) to `el`. */
export function attachStroke(
  stroke: InkStroke,
  el: AttachElement,
  elements: ElementLookup = {},
): InkStroke {
  const board = strokeBoardPoints(stroke, elements) ?? decodePoints(stroke.points);
  return {
    ...stroke,
    attachedTo: el.id,
    attachedSize: { w: el.w, h: el.h },
    points: encodePoints(boardToLocal(board, el)),
  };
}

/** Detaches a stroke: its points become board coordinates at the element's current transform. */
export function detachStroke(stroke: InkStroke, elements: ElementLookup): InkStroke {
  const board = strokeBoardPoints(stroke, elements) ?? decodePoints(stroke.points);
  const { attachedTo: _a, attachedSize: _s, ...rest } = stroke;
  return { ...rest, points: encodePoints(board) };
}

/** Strokes attached to any of the deleted elements (deletion cascade, decision I3). */
export function strokesAttachedTo(
  strokes: readonly InkStroke[],
  elementIds: Iterable<string>,
): string[] {
  const ids = new Set(elementIds);
  return strokes
    .filter((s) => s.attachedTo !== undefined && ids.has(s.attachedTo))
    .map((s) => s.id);
}
